import { useEffect, useRef, useState } from "react";
import useValidationErrors from "@util/hook/useValidationErrors";
import useDocMembershipSuspendApi from "@apis/useDocMembershipSuspendApi";
import { isEmpty, isEqual, isNumber } from "@util/utils";
import { isValidDate } from "@util/moment";

// 功能：暫停申請書表單流程（初始化、range 重算、暫存、上傳、送出）
const useMembershipSuspensionForm = ({
	formAdapter,
	isReadOnly,
	contractIDProps,
	referenceIDProps,
	onSubmitSuccess,
	onSaveDraftSuccess,
}) => {
	const [options, setOptions] = useState({
		coachOptions: null,
		suspendTypeOptions: null,
		suspendReasonTypeOptions: null,
		paymentTypeOptions: null,
	});
	const [isMounted, setMounted] = useState(false);
	const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();
	const {
		getDocSuspendContractApi,
		postDocSuspendContractApi,
		postDocTemporarySuspendContractApi,
		postUploadDocMembershipSuspendPlugin,
		postDocMembershipSuspendRange,
	} = useDocMembershipSuspendApi();

	const uploadFileRef = useRef(null);
	const pathRef = useRef(null);
	const tokenRef = useRef(null);
	const apiSendingRef = useRef(false);

	const [formData, setFormData] = useState({
		membershipID: null,
		contractID: null,
		memberID: null,
		plugIns: null,
		applyDate: null,
		employeeID: null,
		isConfirm: null,
		checkNextMembershipDebit: null,
		leaveReason: null,
		suspendType: null,
		suspendReasonType: null,
		suspendReasonText: null,
		isKeepDebit: null,
		suspendDay: null,
		suspendMonth: null,
		isAllowSuspend: null,
		isCompleteContract: null,
		signImg: null,
		contractVersion: null,
		contractVersionText: null,
		hasNewMembership: null,
		suspensionDate: null,
		recoveryDate: null,
		suspendStartDate: null,
		suspendEndDate: null,
		warningMessage: null,
		paymentType: null,
		debitDayOfMonth: null,
		membershipStartDate: null,
		membershipEndDate: null,
		membershipExtensionDate: null,
		visibleOverNextSuspend: null,
		unusedMonth: null,
		unusedDay: null,
		usedMonth: null,
		usedDay: null,
		hasDeviceOrder: null,
	});

	const buildFormData = () => {
		if (isReadOnly) return formData;
		const { plugIns, ...fieldData } = formAdapter.getFormData();
		return {
			...formData,
			...fieldData,
			plugIns: {
				...plugIns,
				path: pathRef.current,
				token: tokenRef.current,
			},
		};
	};

	// 設置區間參數 api回來的
	const setDateRangeParams = (params = {}) => {
		setFormData(prev => ({
			...prev,
			...(params.isAllowSuspend
				? {
					membershipStartDate: params?.membershipStartDate,
					membershipEndDate: params?.membershipEndDate,
					membershipExtensionDate: params?.membershipExtensionDate,
					suspendEndDate: params?.suspendEndDate,
					suspendStartDate: params?.suspendStartDate,
					suspensionDate: params?.suspensionDate,
					recoveryDate: params?.recoveryDate,
					visibleOverNextSuspend: params?.visibleOverNextSuspend,
					paymentType: params?.paymentType,
					debitDayOfMonth: params?.debitDayOfMonth,
					hasNewMembership: params?.hasNewMembership,
				}
				: {}),
			isAllowSuspend: params?.isAllowSuspend,
			warningMessage: params?.warningMessage || "",
		}));
	};

	/** 畫面資料異動：暫停假別、暫停開始時間、暫停月數與暫停天數 */
	const getRangeParams = (sourceData = formData) => {
		const {
			applyDate: applyDateParam,
			contractID: contractIDParam,
			suspendType: suspendTypeParam,
			isKeepDebit: isKeepDebitParam,
			suspendStartDate: suspendStartDateParam,
			suspendMonth: suspendMonthParam,
			suspendDay: suspendDayParam,
			memberID: memberIDParam,
		} = sourceData;

		return {
			applyDate: applyDateParam || null,
			contractID: contractIDParam || 0,
			suspendType: suspendTypeParam,
			suspendStartDate: suspendStartDateParam,
			suspendMonth: suspendMonthParam,
			isKeepDebit: isKeepDebitParam,
			suspendDay: suspendDayParam || 0, // 原件之後要改 undefined時裡面帶0
			memberID: memberIDParam,
		};
	};

	// 送出 按鈕
	const handleSubmitCreate = () => {
		if (apiSendingRef.current) return false;
		clearValidationErrors();
		const isValid = formAdapter.validateForm();
		const file = uploadFileRef.current;
		const payload = {
			...buildFormData(),
			signImg: formAdapter.getSignImg(),
		};
		setFormData(payload);
		if (isValid) {
			apiSendingRef.current = true;
			if (file) {
				postUploadFileDataApi(file, contractIDProps, payload);
			} else {
				doMembershipSuspensionContractApi(contractIDProps, payload);
			}
		}
	};

	// 點擊暫存 按鈕
	const handleSubmitTemporary = () => {
		if (apiSendingRef.current) return false;
		const payload = buildFormData();
		setFormData(payload);
		apiSendingRef.current = true;
		doPostTemporaryDataApi(payload);
	};

	// 上傳請假證明 事件
	const handleFileChange = result => {
		uploadFileRef.current = result?.file ?? null;
	};

	// 上傳文件 API
	const postUploadFileDataApi = async (file, contractID, payload) => {
		const formImgData = new FormData();
		formImgData.append("file", file, file.name);
		formImgData.append("contractID", contractID);
		const resp = await postUploadDocMembershipSuspendPlugin(contractID, formImgData);
		if (resp) {
			pathRef.current = resp;
			tokenRef.current = resp;
			const nextPayload = {
				...payload,
				plugIns: {
					...payload.plugIns,
					path: resp,
					token: resp,
				},
			};
			setFormData(nextPayload);
			doMembershipSuspensionContractApi(contractIDProps, nextPayload);
		} else {
			apiSendingRef.current = false;
		}
	};

	// 暫存 會籍暫停合約資料 API
	const doPostTemporaryDataApi = async params => {
		const resp = await postDocTemporarySuspendContractApi(params);
		if (resp) {
			onSaveDraftSuccess && onSaveDraftSuccess();
		} else {
			apiSendingRef.current = false;
		}
	};

	// 新增 會籍暫停合約資料 API
	const doMembershipSuspensionContractApi = async (contractID, params) => {
		const resp = await postDocSuspendContractApi(contractID, params);
		const { isError = false, result } = resp || {};

		if (isError) {
			// 0002 錯誤
			updateValidationErrors(result);
		} else if (isEmpty(resp)) {
			// 失敗
		} else {
			// 成功
			pathRef.current = resp.plugInPath;
			setFormData(prev => ({
				...prev,
				plugIns: {
					...prev.plugIns,
					path: resp.plugInPath,
				},
			}));
			onSubmitSuccess && onSubmitSuccess(resp);
		}
		apiSendingRef.current = false;
	};

	// 初始會籍暫停資料 API
	const doInitDocMembershipSuspendDataApi = async () => {
		const resp = await getDocSuspendContractApi(contractIDProps, { id: referenceIDProps });
		if (resp) {
			const { resource, ...others } = resp;
			setFormData(others);
			setOptions(resource);
			setMounted(true);
		}
	};

	// 檢查api range回來的資料
	const postCheckDateRangeApi = async params => {
		const { suspendType, suspendStartDate, suspendMonth, suspendDay } = params;
		if (!suspendType || !isValidDate(suspendStartDate) || !isNumber(suspendMonth) || !isNumber(suspendDay)) return;
		const resp = await postDocMembershipSuspendRange(params);
		if (resp) {
			setDateRangeParams(resp);
		}
	};

	const handleChangeApplyDate = (e, value) => {
		const nextFormData = { ...formData, applyDate: value };
		setFormData(nextFormData);
		const params = getRangeParams(nextFormData);
		postCheckDateRangeApi(params);
	};

	// 轉換 暫停假別 事件
	const handleSuspendTypeChange = (e, value) => {
		const nextFormData = {
			...formData,
			suspendType: value,
			// 暫停只能請月
			suspendDay: isEqual(value, 1) ? 0 : formData.suspendDay,
		};
		setFormData(nextFormData);
		const params = getRangeParams(nextFormData);
		postCheckDateRangeApi(params);
	};

	// 暫停原因 事件 如果是其他原因說明為必填 9 為其他
	const handleSuspendReasonChange = (e, value) => {
		setFormData(prev => ({ ...prev, suspendReasonType: value }));
	};

	// 變換 暫停開始時間 事件
	const handleSuspendDataChange = (e, value) => {
		const nextFormData = { ...formData, suspendStartDate: value };
		setFormData(nextFormData);
		const params = getRangeParams(nextFormData);
		postCheckDateRangeApi(params);
	};

	// 變換 暫停多久-月 暫停多久-日 事件
	const handleSuspendMonthChange = (e, value) => {
		const nextFormData = { ...formData, suspendMonth: value };
		setFormData(nextFormData);
		const params = getRangeParams(nextFormData);
		postCheckDateRangeApi(params);
	};

	const handleSuspendDayChange = (e, value) => {
		const nextFormData = { ...formData, suspendDay: value };
		setFormData(nextFormData);
		const params = getRangeParams(nextFormData);
		postCheckDateRangeApi(params);
	};

	const handleExtensionChange = (e, value) => {
		const nextFormData = { ...formData, isKeepDebit: value };
		setFormData(nextFormData);
		const params = getRangeParams(nextFormData);
		postCheckDateRangeApi(params);
	};

	useEffect(
		() => {
			doInitDocMembershipSuspendDataApi();
			return () => setMounted(false);
		},
		// eslint-disable-next-line
		[]
	);

	return {
		options, isMounted, validationErrors, formData,
		handleSubmitCreate, handleSubmitTemporary, handleFileChange,
		handleChangeApplyDate, handleSuspendTypeChange, handleSuspendReasonChange,
		handleSuspendDataChange, handleSuspendMonthChange, handleSuspendDayChange, handleExtensionChange,
	};
};

export default useMembershipSuspensionForm;
